import axios, { AxiosInstance } from 'axios';
import { Upload } from 'tus-js-client';
import * as tus from 'tus-js-client';
import { VimeoUpload } from '@/external/vimeo-upload/vimeo-upload';

const VIMEO_UPLOAD_URL = 'https://api.vimeo.com/me/videos';
// No trailing slash: every call appends a path starting with "/", and Vimeo
// 308-redirects "//" paths, which browsers reject on CORS preflight.
const VIMEO_API_URL = 'https://api.vimeo.com';
const VIMEO_HEADERS = {
  Accept: 'application/vnd.vimeo.*+json;version=3.4',
  'Content-Type': 'application/json',
  Authorization: `Bearer ${process.env.VUE_APP_VIMEO_TOKEN}`,
};
const api: AxiosInstance = axios.create({ headers: VIMEO_HEADERS });

async function prepareVimeoUpload(
  file: File,
  description: string
): Promise<any> {
  const data: any = {
    upload: {
      approach: 'tus',
      size: file.size,
    },
    name: file.name,
    description: description,
    privacy: {
      view: 'disable',
      embed: 'public',
      download: 'false',
    },
  };
  const response = await api.post(VIMEO_UPLOAD_URL, data);
  return response.data;
}

async function uploadVideoThumbnail(videoId, file) {
  const { type } = file;
  const videoMetaRes = await api.get(`${VIMEO_API_URL}/videos/${videoId}`);
  const thumbnailUri = videoMetaRes.data.metadata.connections.pictures.uri;
  const uploadData = await api.post(`${VIMEO_API_URL}${thumbnailUri}`);
  const headers = {
    Accept: 'application/vnd.vimeo.*+json;version=3.4',
    'Content-Type': type,
  };
  await axios.put(uploadData.data.link, file, { headers });
  await api.patch(`${VIMEO_API_URL}${uploadData.data.uri}`, { active: true });
  const newVideoMetaRes = await api.get(`${VIMEO_API_URL}/videos/${videoId}`);
  return newVideoMetaRes.data;
}

function getThumbnailUrl(videoMeta) {
  const thumbnailUrl = videoMeta.pictures?.sizes[0]['link'].split('_')[0];
  return thumbnailUrl;
}

async function removeThumbnailActive(videoId) {
  const picturesRes = await api.get(
    `${VIMEO_API_URL}/videos/${videoId}/pictures`
  );
  const activePicture = picturesRes.data?.data?.find(({ active }) => active);

  if (activePicture) {
    await api.patch(`${VIMEO_API_URL}${activePicture.uri}`, { active: false });
  }
}

async function tusVimeoUpload(file, uploadUrl, onProgressCallback) {
  const uploadedInfo: Upload = await new Promise((resolve, reject) => {
    /*
     * NOTE: This high-end pro tip might not apply to general-purpose resumable uploads.
     * But if you're using a tus library that breaks the video file into separate chunks
     * based on a maximum size value, we recommend setting the chunk size in the range of
     * 128–512 MB. Smaller chunk sizes might result in slower upload speeds,
     * especially when there are many chunks.
     * */
    const oneMB = 1048576; // bytes
    const upload = new tus.Upload(file, {
      uploadUrl: uploadUrl,
      removeFingerprintOnSuccess: true,
      chunkSize: 128 * oneMB, // 128MB
      retryDelays: [
        0,
        1000,
        3000,
        5000,
        9000,
        13000,
        17000,
        23000,
        29000,
        37000,
        50000,
        70000,
        90000,
        120000,
        200000,
        300000,
        600000,
      ],
      onShouldRetry: function (err, retryAttempt, options) {
        console.log(
          'Vimeo upload error. retryAttempt=' +
            retryAttempt +
            '. Will retry. Err: ',
          err
        );
        return true;
      },
      onProgress: function (bytesUploaded, bytesTotal) {
        console.log('onProgress ' + bytesUploaded + ' / ' + bytesTotal);
        onProgressCallback(Math.ceil((100 * bytesUploaded) / bytesTotal));
      },
      onError: function (error) {
        console.error('Failed because: ' + error);
        reject(error);
      },
      onSuccess: function () {
        resolve(upload);
      },
    });
    upload.start();
  });

  return uploadedInfo;
}

async function streamingVimeoUpload(
  file,
  onProgressCallback
): Promise<{ uri: string }> {
  const vimeoUpload = new VimeoUpload();
  const options = {
    token: process.env.VUE_APP_VIMEO_TOKEN,
    file,
    privacy: {
      view: 'unlisted',
      embed: 'public',
    },
  };
  vimeoUpload.start(options);
  const vimeoId = await new Promise((resolve, reject) => {
    vimeoUpload.on('vimeouploadcomplete', async event => {
      resolve(event.detail.id);
    });

    vimeoUpload.on('totalprogresschanged', event => {
      onProgressCallback(event.detail);
    });

    vimeoUpload.on('vimeouploaderror', event => {
      reject();
    });
  });

  return {
    uri: `/${vimeoId}`,
  };
}

async function getTrimVideoStatus(videoId) {
  const response = await api.get(`${VIMEO_API_URL}/videos/${videoId}/trim`);
  return response;
}

async function trimVideo(videoId, trimStart, trimEnd) {
  const payload = {
    trim_start: trimStart,
    trim_end: trimEnd,
  };
  const response = await api.post(
    `${VIMEO_API_URL}/videos/${videoId}/trim`,
    payload
  );
  return response;
}

export default {
  prepareVimeoUpload,
  uploadVideoThumbnail,
  getThumbnailUrl,
  removeThumbnailActive,
  tusVimeoUpload,
  streamingVimeoUpload,
  getTrimVideoStatus,
  trimVideo,
};
